import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTheme } from '@mui/material/styles';

import axisDiagramRight from '../../../../../assets/mechanical_axis_diagram_R_flat.svg';
import axisDiagramLeft from '../../../../../assets/mechanical_axis_diagram_L_flat.svg';

import { setActiveTab } from '../../../../../actions/casesActions';

import { caseConstants } from '../../../../../constants';

import '../../../../modals/case/case-modal.scss';

const MechanicalAxis = ({ activeCase, initialTibialWidth = 0, PTS }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [rangeData, toggleRangeData] = useState(false);

  const { calculationDataCoronal, calculationDataSaggital, procedureSides } = caseConstants;
  const { alignmentData = {} } = activeCase;

  const getRangeClass = (value, min, max) => {
    if (value === min || value === max) {
      return 'orange';
    }
    if (value > min && value < max) {
      return 'green';
    }
    if (value < min || value > max) {
      return 'red';
    }
  };

  const tooltipText =
    'Three-dimensional analysis of patient’s preoperative Mechanical Alignment\n' +
    '\n' +
    'Measurement colour codes:\n' +
    '\n' +
    'Green - Within normative range\n' +
    'Orange - Boundary of normative range\n' +
    'Red - Outside normative range\n' +
    '\n' +
    'Image is an animated representation of the resultant deformity correction\n' +
    'only and should not be referenced for any clinical decision making';

  const tooltipSx = {
    padding: theme.spacing(2),
    fontSize: '1rem',
    whiteSpace: 'pre-line',
  };

  return (
    <div className="modal-item-block flex-1 height-100 margin-0">
      <div className="block-header">Current Alignment</div>

      <div className="d-flex space-between">
        <div className="width-50">
          <div className="alignment-data-item">
            <div>
              <div>HKA (°) '{alignmentData?.type === 'Valgus' ? 'Valgus' : 'Varus'}'</div>
              <div className="alignment-data-value">{alignmentData['Pre-Op_HKA']}</div>
            </div>
          </div>

          <div className="alignment-data-item">
            <div>
              <div>
                WBL <span className="secondary">(%)</span>
              </div>
              <div className="alignment-data-value">{initialTibialWidth}</div>
            </div>
          </div>
        </div>

        <div className="d-flex direction-column align-end height-100">
          <FormControlLabel
            checked={rangeData}
            control={<Switch color="primary" />}
            label="Range Data"
            labelPlacement="start"
            onChange={(e) => toggleRangeData(e.target.checked)}
          />

          <div className="d-flex">
            <Tooltip
              title="Files"
              placement="left"
              enterTouchDelay={50}
              leaveTouchDelay={4000}
              slotProps={{ tooltip: { sx: tooltipSx } }}
            >
              <IconButton onClick={() => dispatch(setActiveTab(2))} color="primary">
                <DescriptionIcon />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={tooltipText}
              placement="left"
              enterTouchDelay={50}
              leaveTouchDelay={4000}
              slotProps={{ tooltip: { sx: tooltipSx } }}
            >
              <InfoIcon color="primary" />
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="axis-diagram">
        <div className="image-container">
          {activeCase?.procedureSide === procedureSides.left && (
            <img src={axisDiagramLeft} alt="axis-diagram-left" />
          )}
          {activeCase?.procedureSide === procedureSides.right && (
            <img src={axisDiagramRight} alt="axis-diagram-right" />
          )}

          {calculationDataCoronal.map((item) => (
            <div
              key={item.field}
              className={`axis-diagram-field ${item.field} ${activeCase?.procedureSide}`}
            >
              <div>
                <div className="axis-diagram-label">
                  {item.label}:{' '}
                  <span className={getRangeClass(alignmentData[item.field], item.min, item.max)}>
                    {alignmentData[item.field]}°
                  </span>
                </div>
                {rangeData && (
                  <div className="secondary font-size-xs">
                    ({item.min} - {item.max})
                  </div>
                )}
              </div>
            </div>
          ))}

          {calculationDataSaggital.map((item) => (
            <div
              key={item.field}
              className={`axis-diagram-field ${item.field} ${activeCase?.procedureSide}`}
            >
              <div>
                <div className="axis-diagram-label">
                  {item.label}:{' '}
                  <span
                    className={getRangeClass(
                      item.field === 'PPTA' ? PTS : alignmentData[item.field],
                      item.min,
                      item.max
                    )}
                  >
                    {item.field === 'PPTA' ? PTS : alignmentData[item.field]}°
                  </span>
                </div>
                {rangeData && item.min && item.max && (
                  <div className="secondary font-size-xs">
                    ({item.min} - {item.max})
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="axis-diagram-labels secondary">
        <div>{activeCase.procedureSide === procedureSides.left ? 'Coronal' : 'Sagittal'}</div>
        <div>{activeCase.procedureSide === procedureSides.left ? 'Sagittal' : 'Coronal'}</div>
      </div>
    </div>
  );
};

MechanicalAxis.propTypes = {
  activeCase: PropTypes.object,
};

export default MechanicalAxis;
